import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as CapacitorIAP from "@/iap/capacitorIAP";
import { APPLE_PRODUCT_IDS } from "@/iap/products";

const PRODUCT_IDS = APPLE_PRODUCT_IDS;

export const useInAppPurchase = () => {
  const [isIOS, setIsIOS] = useState(false);
  const [products, setProducts] = useState<CapacitorIAP.IAPProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isIOSNative = platform === "ios" && Capacitor.isNativePlatform();
    setIsIOS(isIOSNative);
    console.log("[CAPACITOR-IAP] Platform:", platform, "Native:", Capacitor.isNativePlatform());
    if (isIOSNative) initializeIAP();
  }, []);

  useEffect(() => {
    if (!isIOS) return;
    const listener = CapacitorIAP.onIapLog((e: any) => {
      console.log("[CAPACITOR-IAP] Native log:", e.message || e);
    });
    return () => listener?.remove?.();
  }, [isIOS]);

  const initializeIAP = useCallback(async () => {
    console.log("[CAPACITOR-IAP] Starting initialization...");
    setIsLoading(true);
    setInitError(null);
    try {
      if (!CapacitorIAP.ready()) throw new Error("Capacitor IAP plugin not available");
      console.log("[CAPACITOR-IAP] Plugin ready, fetching products...");
      const productList = await CapacitorIAP.getProducts([PRODUCT_IDS.monthly, PRODUCT_IDS.annual]);
      console.log("[CAPACITOR-IAP] Products loaded:", productList.length);
      setProducts(productList);
      setIsInitialized(true);
    } catch (error: any) {
      console.error("[CAPACITOR-IAP] Initialization error:", error);
      setInitError(error.message || "Erreur d'initialisation IAP");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ------ ACHAT ------
  const purchaseProduct = useCallback(
    async (productId: string, familyId: string): Promise<boolean> => {
      console.log("[CAPACITOR-IAP] Starting purchase:", productId);
      setIsLoading(true);
      try {
        if (!CapacitorIAP.ready()) {
          throw new Error("IAP plugin not available");
        }

        const res = await CapacitorIAP.purchase(productId);
        console.log("[CAPACITOR-IAP] Purchase result:", res);

        if (res.status !== "purchased" || !res.transactionId) {
          // Don't throw here - user may have cancelled or purchase is pending
          if (res.status === "failed" && res.errorMessage?.toLowerCase().includes("cancel")) {
            console.log("[CAPACITOR-IAP] Purchase cancelled by user");
            return false;
          }
          throw new Error(res.errorMessage || "Achat non finalisé");
        }

        // Session utilisateur
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) throw new Error("No active session");

        // Vérification serveur avec transactionId (StoreKit 2)
        console.log("[CAPACITOR-IAP] Verifying purchase with backend...");
        const { data, error } = await supabase.functions.invoke("verify-apple-receipt", {
          body: {
            platform: "ios",
            product_id: productId,
            family_id: familyId,
            transaction_id: res.transactionId,
            transaction_details: res.transactionDetails ?? null,
          },
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        });
        
        if (error) {
          console.error("[CAPACITOR-IAP] Backend verification error:", error);
          toast.error("Erreur de vérification. Veuillez réessayer ou restaurer vos achats.");
          return false;
        }

        console.log("[CAPACITOR-IAP] Verification successful:", data);
        toast.success("Abonnement activé avec succès !");
        return true;
      } catch (error: any) {
        console.error("[CAPACITOR-IAP] Purchase error:", error);
        const errorMessage = error.message || "Erreur lors de l'achat";
        // Only show error toast if it's not a cancellation
        if (!errorMessage.includes("cancelled")) {
          toast.error(`${errorMessage}. Veuillez réessayer.`);
        }
        return false;
      } finally {
        // Always re-enable buttons after purchase attempt
        setIsLoading(false);
      }
    },
    []
  );

  // ------ RESTORE ------
  const restorePurchases = useCallback(
    async (familyId: string): Promise<boolean> => {
      console.log("[CAPACITOR-IAP] Restoring purchases...");
      setIsLoading(true);
      try {
        if (!CapacitorIAP.ready()) throw new Error("IAP plugin not available");

        const restored = await CapacitorIAP.restore();
        if (restored.length === 0) {
          toast.info("Aucun achat à restaurer");
          return false;
        }

        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) throw new Error("No active session");

        let ok = 0;
        for (const p of restored) {
          if (!p.transactionId) continue;

          const { error } = await supabase.functions.invoke("verify-apple-receipt", {
            body: {
              platform: "ios",
              product_id: p.productId,
              family_id: familyId,
              transaction_id: p.transactionId,                     // <--
              transaction_details: p.transactionDetails ?? null,    // <--
            },
            headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
          });

          if (error) {
            console.error("[CAPACITOR-IAP] Restore verification error:", error);
          } else {
            ok++;
            console.log("[CAPACITOR-IAP] Purchase restored:", p.productId);
          }
        }

        if (ok > 0) {
          toast.success(`${ok} achat(s) restauré(s) avec succès`);
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[CAPACITOR-IAP] Restore error:", error);
        toast.error(error.message || "Erreur lors de la restauration");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isIOS,
    isInitialized,
    products,
    isLoading,
    initError,
    purchaseProduct,
    restorePurchases,
    retryInitialization: initializeIAP,
    PRODUCT_IDS,
  };
};
